/**
 * useHomeData Hooks
 * 
 * React hooks for fetching home page data - collections and best seller products.
 */

import { useState, useEffect, useCallback } from 'react';
import { getCollectionService } from '../../infrastructure/services/collection.service';
import { usePricingContext } from '@/lib/hooks/use-pricing-context';
import {
  CollectionEntity,
  CollectionProduct,
} from '../../domain/entities/collection.entity';

// Constants for home page data
const HOME_COLLECTIONS_LIMIT = 8;
const BEST_SELLERS_COLLECTION_SLUG = 'best-sellers';
const BEST_SELLERS_PRODUCTS_LIMIT = 8;
const WEEKLY_DEALS_COLLECTION_SLUG = 'weekly-deals';
const WEEKLY_DEALS_PRODUCTS_LIMIT = 8;
const YOULL_ALSO_LOVE_COLLECTION_SLUG = 'youll-also-love';
const YOULL_ALSO_LOVE_PRODUCTS_LIMIT = 5;

/**
 * Extended collection entity that includes a product image for display
 */
export interface HomeCollectionWithImage extends CollectionEntity {
  displayImage?: string;
}

interface UseHomeCollectionsResult {
  collections: HomeCollectionWithImage[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Hook to fetch collections for the home page category section.
 * Returns up to 8 active collections with product images for display.
 * For collections without an imageUri, fetches a random product image.
 */
export function useHomeCollections(): UseHomeCollectionsResult {
  const [collections, setCollections] = useState<HomeCollectionWithImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCollections = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const service = getCollectionService();
      const result = await service.getCollections(1, HOME_COLLECTIONS_LIMIT);
      
      // Filter only active collections with products
      const activeCollections = result.collections
        .filter((c) => c.status === 'active' && c.productCount > 0)
        .slice(0, HOME_COLLECTIONS_LIMIT);
      
      // For collections without images, fetch product images in parallel
      const collectionsWithImages: HomeCollectionWithImage[] = await Promise.all(
        activeCollections.map(async (collection) => {
          // If collection has its own image, use it
          if (collection.imageUri) {
            return {
              ...collection,
              displayImage: collection.imageUri,
            };
          }
          
          // Otherwise, try to fetch a product image from the collection
          try {
            const collectionDetails = await service.getCollectionBySlug(collection.slug, { limit: 1 });
            const firstProduct = collectionDetails.products[0];
            
            return {
              ...collection,
              displayImage: firstProduct?.imageUri || undefined,
            };
          } catch {
            // If fetching fails, return collection without product image
            return {
              ...collection,
              displayImage: undefined,
            };
          }
        })
      );
      
      setCollections(collectionsWithImages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch collections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  return {
    collections,
    loading,
    error,
    refresh: fetchCollections,
  };
}

export interface CollectionCardProduct {
  id: string;
  slug: string;
  name: string;
  image: string;
  price: number;
  compareAtPrice?: number;
  rating?: number;
  reviewCount?: number;
}

interface UseCollectionProductsResult {
  products: CollectionCardProduct[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

function mapCollectionProducts(
  products: CollectionProduct[],
  limit: number,
): CollectionCardProduct[] {
  return products.slice(0, limit).map((product) => ({
    id: product.id,
    slug: product.id,
    name: product.title,
    image: product.imageUri || '',
    // priceMin/priceMax from collection service are in dollars; ProductCard expects cents
    price: Math.round(product.priceMin * 100),
    compareAtPrice:
      product.priceMax > product.priceMin
        ? Math.round(product.priceMax * 100)
        : undefined,
  }));
}

function useCollectionProducts(
  slug: string,
  limit: number,
  errorMessage: string,
): UseCollectionProductsResult {
  const { currency, country } = usePricingContext();
  const [products, setProducts] = useState<CollectionCardProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const service = getCollectionService();
      const result = await service.getCollectionBySlug(slug, { limit, currency, country });
      setProducts(mapCollectionProducts(result.products, limit));
    } catch (err) {
      setError(err instanceof Error ? err.message : errorMessage);
    } finally {
      setLoading(false);
    }
  }, [slug, limit, errorMessage, currency, country]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return { products, loading, error, refresh: fetchProducts };
}

/**
 * Hook to fetch best seller products for the home page carousel.
 */
export function useBestSellerProducts(): UseCollectionProductsResult {
  return useCollectionProducts(
    BEST_SELLERS_COLLECTION_SLUG,
    BEST_SELLERS_PRODUCTS_LIMIT,
    'Failed to fetch best sellers',
  );
}

/**
 * Hook to fetch weekly deals products for the home page carousel.
 */
export function useWeeklyDealsProducts(): UseCollectionProductsResult {
  return useCollectionProducts(
    WEEKLY_DEALS_COLLECTION_SLUG,
    WEEKLY_DEALS_PRODUCTS_LIMIT,
    'Failed to fetch weekly deals',
  );
}

export interface YoullAlsoLoveProduct {
  id: string;
  slug: string;
  title: string;
  image: string;
  descriptionHtml: string;
  price: number;
  compareAtPrice?: number;
  rating?: number;
  reviewCount?: number;
}

interface UseYoullAlsoLoveProductsResult {
  mainProduct: YoullAlsoLoveProduct | null;
  gridProducts: YoullAlsoLoveProduct[];
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch "You'll Also Love" products for the home page.
 * First product is the featured main product; remaining are grid cards.
 */
export function useYoullAlsoLoveProducts(): UseYoullAlsoLoveProductsResult {
  const [mainProduct, setMainProduct] = useState<YoullAlsoLoveProduct | null>(null);
  const [gridProducts, setGridProducts] = useState<YoullAlsoLoveProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const service = getCollectionService();
      const result = await service.getCollectionBySlug(YOULL_ALSO_LOVE_COLLECTION_SLUG, {
        limit: YOULL_ALSO_LOVE_PRODUCTS_LIMIT,
      });

      const mapped: YoullAlsoLoveProduct[] = result.products
        .slice(0, YOULL_ALSO_LOVE_PRODUCTS_LIMIT)
        .map((product: CollectionProduct) => ({
          id: product.id,
          slug: product.id,
          title: product.title,
          image: product.imageUri || '',
          descriptionHtml: product.description || '',
          price: Math.round(product.priceMin * 100),
          compareAtPrice: product.priceMax > product.priceMin
            ? Math.round(product.priceMax * 100)
            : undefined,
        }));

      if (mapped.length > 0) {
        setMainProduct(mapped[0]);
        setGridProducts(mapped.slice(1));
      } else {
        setMainProduct(null);
        setGridProducts([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch recommendations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return { mainProduct, gridProducts, loading, error };
}

